import {apiSlice} from './apiSlice';

/**
 * Endpoints tool Nội dung AI tự động (§9 — AI Automatic Content).
 *
 * Pipeline viết bài chạy NỀN phía BE (tick omni-content-tick): tạo bài chỉ xếp hàng,
 * FE poll getContentItem (5s) khi bài đang ở trạng thái chạy (running=true). Trạng thái
 * chờ duyệt (outline_review/review) chuyển tiếp bằng approveContentItem theo review_mode.
 * Tag: ContentItem theo dự án + theo bài; ContentSettings theo dự án.
 */
export const contentApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getContentSettings: builder.query({
			query: (projectId) => ({url: `content/projects/${projectId}/settings`, method: 'get'}),
			transformResponse: (body) => body?.data || {},
			providesTags: (result, error, projectId) => [{type: 'ContentSettings', id: `project-${projectId}`}],
		}),
		updateContentSettings: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/settings`, method: 'put', data}),
			transformResponse: (body) => body?.data || {},
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentSettings', id: `project-${projectId}`}],
		}),
		getContentItems: builder.query({
			query: (projectId) => ({url: `content/projects/${projectId}/items`, method: 'get'}),
			transformResponse: (body) => body?.data || {items: [], quota: {}, available: false},
			providesTags: (result, error, projectId) => [{type: 'ContentItem', id: `project-${projectId}`}],
		}),
		createContentItem: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/items`, method: 'post', data}),
			transformResponse: (body) => body?.data || null,
			// Trừ quota bài → cập nhật Subscription; refetch danh sách.
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				'Subscription',
			],
		}),
		getContentItem: builder.query({
			query: ({projectId, itemId}) => ({url: `content/projects/${projectId}/items/${itemId}`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, {itemId}) => [{type: 'ContentItem', id: `item-${itemId}`}],
		}),
		approveContentItem: builder.mutation({
			query: ({projectId, itemId}) => ({url: `content/projects/${projectId}/items/${itemId}/approve`, method: 'post'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId, itemId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				{type: 'ContentItem', id: `item-${itemId}`},
			],
		}),
		retryContentItem: builder.mutation({
			query: ({projectId, itemId}) => ({url: `content/projects/${projectId}/items/${itemId}/retry`, method: 'post'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId, itemId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				{type: 'ContentItem', id: `item-${itemId}`},
			],
		}),
		updateContentItem: builder.mutation({
			query: ({projectId, itemId, ...data}) => ({url: `content/projects/${projectId}/items/${itemId}`, method: 'put', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId, itemId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				{type: 'ContentItem', id: `item-${itemId}`},
			],
		}),
		deleteContentItem: builder.mutation({
			query: ({projectId, itemId}) => ({url: `content/projects/${projectId}/items/${itemId}`, method: 'delete'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentItem', id: `project-${projectId}`}],
		}),
		// Đợt 2: thay/upload ảnh 1 slot (data = data URL base64).
		replaceContentImage: builder.mutation({
			query: ({projectId, itemId, ...data}) => ({url: `content/projects/${projectId}/items/${itemId}/image`, method: 'post', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId, itemId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				{type: 'ContentItem', id: `item-${itemId}`},
			],
		}),
		// Đợt 2: kế hoạch bài viết hàng loạt.
		getContentPlans: builder.query({
			query: (projectId) => ({url: `content/projects/${projectId}/plans`, method: 'get'}),
			transformResponse: (body) => body?.data || {plans: []},
			providesTags: (result, error, projectId) => [{type: 'ContentPlan', id: `project-${projectId}`}],
		}),
		previewContentPlan: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/plans/preview`, method: 'post', data}),
			transformResponse: (body) => body?.data || {articles: []},
			// Tốn token AI → cập nhật Subscription (không tốn quota bài).
			invalidatesTags: ['Subscription'],
		}),
		createContentPlan: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/plans`, method: 'post', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'ContentPlan', id: `project-${projectId}`},
				{type: 'ContentItem', id: `project-${projectId}`},
				'Subscription',
			],
		}),
		updateContentPlan: builder.mutation({
			query: ({projectId, planId, ...data}) => ({url: `content/projects/${projectId}/plans/${planId}`, method: 'put', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentPlan', id: `project-${projectId}`}],
		}),
		deleteContentPlan: builder.mutation({
			query: ({projectId, planId}) => ({url: `content/projects/${projectId}/plans/${planId}`, method: 'delete'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'ContentPlan', id: `project-${projectId}`},
				{type: 'ContentItem', id: `project-${projectId}`},
			],
		}),
		// Đợt 3 (GĐ2): kênh đăng + đăng bài chạy nền.
		getContentTargets: builder.query({
			query: (projectId) => ({url: `content/projects/${projectId}/targets`, method: 'get'}),
			transformResponse: (body) => body?.data || {targets: [], types: []},
			providesTags: (result, error, projectId) => [{type: 'ContentTarget', id: `project-${projectId}`}],
		}),
		// Đợt 4: kiểm tra kết nối + lấy danh sách blog (Shopify-like) TRƯỚC khi lưu kênh.
		probeContentTarget: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/targets/probe`, method: 'post', data}),
			transformResponse: (body) => body?.data || {blogs: [], need_blog: false},
		}),
		createContentTarget: builder.mutation({
			query: ({projectId, ...data}) => ({url: `content/projects/${projectId}/targets`, method: 'post', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentTarget', id: `project-${projectId}`}],
		}),
		updateContentTarget: builder.mutation({
			query: ({projectId, targetId, ...data}) => ({url: `content/projects/${projectId}/targets/${targetId}`, method: 'put', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentTarget', id: `project-${projectId}`}],
		}),
		deleteContentTarget: builder.mutation({
			query: ({projectId, targetId}) => ({url: `content/projects/${projectId}/targets/${targetId}`, method: 'delete'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'ContentTarget', id: `project-${projectId}`}],
		}),
		publishContentItem: builder.mutation({
			query: ({projectId, itemId, ...data}) => ({url: `content/projects/${projectId}/items/${itemId}/publish`, method: 'post', data}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId, itemId}) => [
				{type: 'ContentItem', id: `project-${projectId}`},
				{type: 'ContentItem', id: `item-${itemId}`},
			],
		}),
	}),
});

export const {
	useGetContentSettingsQuery,
	useUpdateContentSettingsMutation,
	useGetContentItemsQuery,
	useCreateContentItemMutation,
	useGetContentItemQuery,
	useApproveContentItemMutation,
	useRetryContentItemMutation,
	useUpdateContentItemMutation,
	useDeleteContentItemMutation,
	useReplaceContentImageMutation,
	useGetContentPlansQuery,
	usePreviewContentPlanMutation,
	useCreateContentPlanMutation,
	useUpdateContentPlanMutation,
	useDeleteContentPlanMutation,
	useGetContentTargetsQuery,
	useProbeContentTargetMutation,
	useCreateContentTargetMutation,
	useUpdateContentTargetMutation,
	useDeleteContentTargetMutation,
	usePublishContentItemMutation,
} = contentApiSlice;
